import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Table, TagFilters } from '@/components';
import { dateFormatRender, defaultRender } from '@/components/FieldsCard';
import i18n from '@/i18n';
import { cleanObject } from '@/utils';
import { formatAmount } from '@/utils/format';
import useUrlParams from '@/utils/useUrlParams';

import { getTransactions } from './service';

const t = i18n.t.bind(i18n);
interface Transaction {
  amount: string; // 交易金额
  balanceChange: string; // 余额变化（这里有正、有负）
  createdAt: string;
  currency: string; // 货币
  dataCenter: string; // 数据中心
  id: string;
  operator: string; // 操作人
  relatedInvoiceId: string; // 账单id
  relatedOrderItemId: string; // 订单id
  relatedRechargeRequestId: string; // 充值申请id
  remark: string; // 备注
  txType: string; // 交易类型，结算/充值/收入
  walletId: string;
  workspace: string;
}

// 交易类型映射和颜色配置
const TX_TYPE_CONFIG: Record<string, { label: string; color: string }> = {
  SETTLEMENT: { label: t('common:deduction'), color: '#ff4d4f' },
  RECHARGE: { label: t('common:recharge'), color: '#52c41a' },
  INCOME: { label: t('common:income'), color: '#1890ff' },
  CREDIT_REPAY: { label: t('common:quota.return'), color: '#fa8c16' },
  REFUND: { label: t('common:refund'), color: '#722ed1' },
  ADJUSTMENT: { label: t('common:adjustment'), color: '#13c2c2' },
  WECHAT_PAY_RECHARGE: { label: t('common:wechat.recharge'), color: '#52c41a' },
  BANK_TRANSFER_RECHARGE: { label: t('common:corporate.transfer'), color: '#52c41a' },
};

// 表格列定义
const columns = [
  {
    title: 'ID',
    dataIndex: 'id',
    key: 'id',
    width: 150,
    ellipsis: true,
    render: defaultRender,
  },
  {
    title: t('common:transaction.type'),
    dataIndex: 'txType',
    key: 'txType',
    width: 100,
    render: (value: string) => {
      const config = TX_TYPE_CONFIG[value] || { label: value, color: '#666' };
      return (
        <span
          style={{
            color: config.color,
          }}
        >
          {config.label}
        </span>
      );
    },
  },
  {
    //交易前余额
    title: t('common:balance.before.transaction'),
    dataIndex: 'previousBalance',
    key: 'previousBalance',
    width: 120,
    align: 'right' as const,
    render: (value: string) => (
      <span
        style={{
          color: '#666',
          fontFamily: 'monospace',
        }}
      >
        {formatAmount({ amount: value })}
      </span>
    ),
  },
  {
    title: t('common:transaction.amount'),
    dataIndex: 'amount',
    key: 'amount',
    width: 120,
    align: 'right' as const,
    render: (value: string, record: Transaction) => (
      <span
        style={{
          color: parseFloat(value) < 0 ? '#ff4d4f' : '#52c41a',
        }}
      >
        {formatAmount({ amount: value, showSymbol: true })}
      </span>
    ),
  },
  {
    //交易后余额
    title: t('common:balance.after.transaction'),
    dataIndex: 'balance',
    key: 'balance',
    width: 120,
    align: 'right' as const,
    render: (value: string, record: Transaction) => (
      <span
        style={{
          color: '#666',
        }}
      >
        {formatAmount({ amount: value })}
      </span>
    ),
  },
  {
    title: t('common:currency'),
    dataIndex: 'currency',
    key: 'currency',
    width: 80,
    render: (value: string) => t('common:rmb'), // 目前只支持人民币，先写死
  },
  {
    title: t('tenant:transactor'),
    dataIndex: 'operator',
    key: 'operator',
    width: 120,
    ellipsis: true,
    render: defaultRender,
  },
  {
    title: t('common:transaction.time'),
    dataIndex: 'createdAt',
    key: 'createdAt',
    width: 180,
    sorter: true,
    render: dateFormatRender,
  },
  {
    title: t('common:datacenter'),
    dataIndex: 'dataCenter',
    key: 'dataCenter',
    width: 120,
    ellipsis: true,
    render: defaultRender,
  },
  {
    title: t('common:bill.id'),
    dataIndex: 'relatedInvoiceId',
    key: 'relatedInvoiceId',
    width: 150,
    ellipsis: true,
    render: defaultRender,
  },
  {
    title: t('common:order.id'),
    dataIndex: 'relatedOrderItemId',
    key: 'relatedOrderItemId',
    width: 150,
    ellipsis: true,
    render: defaultRender,
  },
  {
    title: t('tenant:recharge.id'),
    dataIndex: 'relatedRechargeRequestId',
    key: 'relatedRechargeRequestId',
    width: 150,
    ellipsis: true,
    render: defaultRender,
  },
  {
    title: t('common:remarks'),
    dataIndex: 'remark',
    key: 'remark',
    width: 200,
    ellipsis: true,
    render: (text: string, record: Transaction) => (
      <span
        style={{
          fontSize: 12,
          color: '#666',
          lineHeight: 1.4,
        }}
        title={text}
      >
        {text?.replace(/CNY/g, '') || '-'}
      </span>
    ),
  },
];

const schema: any[] = [
  {
    key: 'txType[eq]',
    label: t('common:transaction.type'),
    type: 'select',
    options: Object.entries(TX_TYPE_CONFIG).map(([key, config]) => ({
      label: config.label,
      value: key,
    })),
  },
  {
    // 数据中心
    key: 'dataCenter[eq]',
    label: t('common:datacenter'),
    type: 'input',
  },
  {
    // 操作人
    key: 'operator[eq]',
    label: t('tenant:transactor'),
    type: 'input',
  },
  {
    // id
    key: 'id[eq]',
    label: 'ID',
    type: 'input',
  },
  // 账单ID
  {
    key: 'relatedInvoiceId[eq]',
    label: t('common:bill.id'),
    type: 'input',
  },
  // 订单ID
  {
    key: 'relatedOrderItemId[eq]',
    label: t('common:order.id'),
    type: 'input',
  },
  // 充值ID
  {
    key: 'relatedRechargeRequestId[eq]',
    label: t('tenant:recharge.id'),
    type: 'input',
  },
];

export default function Detail() {
  const { t } = useTranslation();
  const { getParam, setParam } = useUrlParams();
  const [data, setData] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
  });

  const query = getParam('query', '{}');

  const getData = async ({ page = pagination.page, limit = pagination.limit, ...rest }) => {
    try {
      setLoading(true);
      const { items, totalItems } = await getTransactions({
        ...rest,
        page,
        limit,
      });

      setData(items || []);
      setPagination((prev) => ({
        ...prev,
        page,
        limit,
        total: totalItems || 0,
      }));
    } catch (error) {
      console.error('获取交易数据失败，请稍后重试', error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  // 处理分页变化
  const handlePagination = ({ limit, page }, { sorter }) => {
    setPagination((prev) => ({ ...prev, limit, page }));
    const query = JSON.parse(getParam('query', '{}'));
    const obj: any = { ...query, page, limit };
    if (sorter?.field && sorter?.direction) {
      obj.sortBy = sorter?.field;
      obj.order = sorter?.direction === 'ascend' ? 'asc' : 'desc';
    } else {
      delete obj.sortBy;
      delete obj.order;
    }
    setParam('query', JSON.stringify(obj));
    getData(obj);
  };

  useEffect(() => {
    refresh();
  }, [query]);

  const refresh = () => {
    getData(JSON.parse(query));
  };

  const handleSearch = (values: Record<string, any>) => {
    const query = JSON.parse(getParam('query', '{}'));
    const newQuery = cleanObject({
      limit: query.limit,
      page: query.page,
      sortBy: query.sortBy,
      order: query.order,
      ...values,
    });
    setParam('query', JSON.stringify(newQuery));
    getData(newQuery);
  };

  return (
    <div
      style={{
        backgroundColor: '#fff',
        borderRadius: 4,
      }}
    >
      <TagFilters
        placeholder={t('search')}
        style={{ padding: 16, paddingBottom: 0, backgroundColor: 'white' }}
        filters={schema}
        resource={schema}
        values={JSON.parse(getParam('query', '{}'))}
        onChange={handleSearch}
        onRefresh={refresh}
      />
      <Table
        data={data}
        columns={columns as any}
        loading={loading}
        pagination={pagination}
        handlePagination={handlePagination}
        rowKey="id"
        scroll={{ x: 1800 }}
      />
    </div>
  );
}
